import { z } from 'zod';

const nameRegex = /^[^±!@£$%^&*_+§¡€#¢§¶•ªº«\\/<>?:;|=.,0-9]{1,30}$/;

const trimString = (u: unknown) => (typeof u === 'string' ? u.trim() : u);

export const name = (message = 'This field is empty or invalid.') =>
  z.preprocess(
    trimString,
    z
      .string()
      .max(30)
      .refine((val) => nameRegex.test(val), { message })
  );

export const nameNullable = (message = 'This field is empty or invalid.') =>
  z.preprocess(
    trimString,
    z
      .string()
      .max(30)
      .nullish()
      .refine((val) => val && nameRegex.test(val), { message })
  );

export const required = (message = 'This field is required.', max = 100) =>
  z.preprocess(
    trimString,
    z
      .string()
      .max(max, {
        message: `The maximum legth of this field is ${max} characters.`,
      })
      .min(1, { message })
  );

// required yet nullable to allow initial values of null or undefined
export const requiredNullable = (
  message = 'This field is required.',
  max = 100
) =>
  z.preprocess(
    trimString,
    z
      .string()
      .max(max, {
        message: `The maximum legth of this field is ${max} characters.`,
      })
      .nullish()
      .refine((value) => value && value.length > 0, {
        message,
      })
  );

export const emailAddress = (message = 'Please enter a valid email address') =>
  z.preprocess(
    trimString,
    z.string().email({ message }).regex(emailRegex, { message })
  );

export const trueOnly = (message = 'Invalid') =>
  z.boolean().refine((val) => val === true, {
    message,
  });

const createRegexSchema = (regex: RegExp, message = 'Invalid format') =>
  z.string().regex(regex, { message });

// Salesforce's EmailAddress rule: its gateway rejects the whole lead if the email fails it.
// Non-ASCII ranges are written as \u escapes. Keep in sync with EmailValidation.Pattern (SubaruComAu.Core).
export const emailRegex =
  /^[a-zA-Z0-9!#$%&'*+\/=?^_`{|}~\-\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u012F\u0134-\u0151\u0154-\u017E\u01CD-\u01F0\u01F4-\u01F5\u01F8-\u021B\u021E-\u021F\u0224-\u0233\u0243\u0246-\u024F\u1E00-\u1E9E\u0180-\u0193\u0197-\u019A\u019D-\u01A1\u01A4-\u01A5\u01AB-\u01B0\u01B2-\u01B6\u1EA0-\u1EFF]+(?:\.[a-zA-Z0-9!#$%&'*+\/=?^_`{|}~\-\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u012F\u0134-\u0151\u0154-\u017E\u01CD-\u01F0\u01F4-\u01F5\u01F8-\u021B\u021E-\u021F\u0224-\u0233\u0243\u0246-\u024F\u1E00-\u1E9E\u0180-\u0193\u0197-\u019A\u019D-\u01A1\u01A4-\u01A5\u01AB-\u01B0\u01B2-\u01B6\u1EA0-\u1EFF]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
export const validateEmail = (email: string) => emailRegex.test(email);

const phoneNumberRegex =
  /^((\+\d{1,3}(-| )?\(?\d{1,3}\)?(-| )?\d{0,3})|(\(?\d{2,3}\)?))(-| )?(\d{3,4})(-| )?(\d{4})(( x| ext)\d{1,5})?$/;

export const phoneNumberSchema = createRegexSchema(
  phoneNumberRegex,
  'Please enter a valid phone number'
);
export const emailAddressSchema = createRegexSchema(
  emailRegex,
  'Please enter a valid email address'
);
export const validatePhone = (phone: string) => phoneNumberRegex.test(phone);

export { z };
